import {format, isValid, isWithinInterval, parse} from 'date-fns';

import {hasTimeFormat} from './hasTimeFormat';
import {normalizeToLocalMidnight} from './normalizeToLocalMidnight';

import type {DateValidationParams} from '../typedef';


const parseDate = (value: string, dateFormat: string) => {
    return parse(value, dateFormat, new Date());
};

export const isFormattedDateValid = (value: string, dateFormat: string): boolean => {
    const parsedDate = parseDate(value, dateFormat);
    return isValid(parsedDate) && format(parsedDate, dateFormat) === value;
};

export const isDateValid = ({
    value,
    dateFormat,
    minDate,
    maxDate,
}: DateValidationParams): boolean => {
    if (!value) {
        return false;
    }

    const parsedDate = parseDate(value, dateFormat);

    return (
        isFormattedDateValid(value, dateFormat) &&
        isDateWithinRange(parsedDate, minDate, maxDate)
    );
};

const isDateWithinRange = (date: Date, minDate: Date, maxDate: Date): boolean => {
    return isWithinInterval(date, {
        start: minDate,
        end: maxDate,
    });
};

export const isDateRangeValid = (
    {value, dateFormat, minDate, maxDate}: DateValidationParams): boolean => {
    if (!value.includes(' to ')) {
        return false;
    }

    const [startDate, endDate] = value.split(' to ').map(date => date.trim());

    if (!isDateValid({value: startDate, dateFormat, minDate, maxDate}) ||
        !isDateValid({value: endDate, dateFormat, minDate, maxDate})) {
        return false;
    }

    const startParsed = parseDate(startDate, dateFormat);
    const endParsed = parseDate(endDate, dateFormat);

    if (!hasTimeFormat(dateFormat)) {
        return normalizeToLocalMidnight(startParsed).getTime() <= normalizeToLocalMidnight(endParsed).getTime();
    }

    return startParsed.getTime() <= endParsed.getTime();
};

export const identifyValueType = ({
    value,
    dateFormat,
    minDate,
    maxDate,
}: DateValidationParams): 'date' | 'dateRange' | 'invalid' => {
    if (isDateValid({value, dateFormat, minDate, maxDate})) {
        return 'date';
    }
    if (isDateRangeValid({value, dateFormat, minDate, maxDate})) {
        return 'dateRange';
    }
    return 'invalid';
};
